import { createContext, useContext, useRef, useCallback, PropsWithChildren } from "react";
import { DRAWER_CACHE_TTL_MS, DRAWER_CACHE_MAX_PER_TYPE } from "@/constants/cache";

type CacheEntry<T = any> = {
  data: T;
  timestamp: number;
  entityId: string;
};

// Cache structure: Map<entityType, { entries: Map<entityId, CacheEntry>, order: string[] }>
// With separate LRU order tracking per type
type TypeCache = {
  entries: Map<string, CacheEntry>;
  order: string[]; // entityIds in access order (oldest first)
};

type DrawerCacheContextType = {
  get: <T>(entityType: string, entityId: string) => T | null;
  set: <T>(entityType: string, entityId: string, data: T) => void;
  invalidate: (entityType: string, entityId?: string) => void;
  isStale: (entityType: string, entityId: string) => boolean;
  clearAll: () => void;
};

const DrawerCacheContext = createContext<DrawerCacheContextType | null>(null);

export function DrawerCacheProvider({ children }: PropsWithChildren) {
  // Cache per entity type: { shift: { entries: Map, order: [] }, project: {...} }
  const cacheRef = useRef<Map<string, TypeCache>>(new Map());

  const getTypeCache = (entityType: string): TypeCache => {
    if (!cacheRef.current.has(entityType)) {
      cacheRef.current.set(entityType, { entries: new Map(), order: [] });
    }
    return cacheRef.current.get(entityType)!;
  };

  const isExpired = (entry: CacheEntry): boolean => Date.now() - entry.timestamp > DRAWER_CACHE_TTL_MS;

  const get = useCallback(<T,>(entityType: string, entityId: string): T | null => {
    const typeCache = getTypeCache(entityType);
    const entry = typeCache.entries.get(entityId);

    if (!entry || isExpired(entry)) {
      if (entry) {
        typeCache.entries.delete(entityId);
        typeCache.order = typeCache.order.filter((id) => id !== entityId);
      }
      return null;
    }

    // Move to end of order (most recently accessed)
    typeCache.order = typeCache.order.filter((id) => id !== entityId);
    typeCache.order.push(entityId);

    return entry.data as T;
  }, []);

  const set = useCallback(<T,>(entityType: string, entityId: string, data: T) => {
    const typeCache = getTypeCache(entityType);

    // Check if this entity is already cached (just updating it)
    const alreadyExists = typeCache.entries.has(entityId);

    // Only evict if we're adding a NEW entry and at capacity
    if (!alreadyExists) {
      while (typeCache.order.length >= DRAWER_CACHE_MAX_PER_TYPE) {
        const oldestId = typeCache.order.shift();
        if (oldestId) typeCache.entries.delete(oldestId);
      }
    }

    typeCache.entries.set(entityId, { data, timestamp: Date.now(), entityId });
    typeCache.order = typeCache.order.filter((id) => id !== entityId);
    typeCache.order.push(entityId);
  }, []);

  const invalidate = useCallback((entityType: string, entityId?: string) => {
    const typeCache = cacheRef.current.get(entityType);
    if (!typeCache) return;

    if (entityId) {
      typeCache.entries.delete(entityId);
      typeCache.order = typeCache.order.filter((id) => id !== entityId);
    } else {
      // Invalidate all entries of this type
      typeCache.entries.clear();
      typeCache.order = [];
    }
  }, []);

  const isStale = useCallback((entityType: string, entityId: string): boolean => {
    const typeCache = cacheRef.current.get(entityType);
    if (!typeCache) return true;
    const entry = typeCache.entries.get(entityId);
    return !entry || isExpired(entry);
  }, []);

  const clearAll = useCallback(() => {
    cacheRef.current.clear();
  }, []);

  return (
    <DrawerCacheContext.Provider value={{ get, set, invalidate, isStale, clearAll }}>
      {children}
    </DrawerCacheContext.Provider>
  );
}

export function useDrawerCache() {
  const context = useContext(DrawerCacheContext);
  if (!context) {
    throw new Error("useDrawerCache must be used within DrawerCacheProvider");
  }
  return context;
}
